/**
 * 報酬セット カテゴリ別 編集画面 (v3.2.6 / T5 Phase 2)。
 *
 * URL: /sales/commissions/:setId/c/:categoryId
 *
 * 表示ロジック:
 *   - カテゴリ名 (ProductCategory.name) と一致する catalog の items を
 *     全件自動表示 (商品の追加・削除は不要)
 *   - 各商品行で type / value / 追加 / 割引 を編集
 *   - 3 タブ (紹介者 / SA / 社員) で listType を切替
 *   - 保存時は: 既存セットの items[] のうち本カテゴリ外を保持しつつ、
 *     本カテゴリ商品行を上書き
 */
import type {
  CommissionItem,
  CommissionListType,
  CommissionType,
} from '@goenhub/shared';
import {
  COMMISSION_LIST_LABELS,
  COMMISSION_LIST_TYPES,
  calculateCommission,
  resolveSelfDevelopedAdditionRate,
} from '@goenhub/shared';
import { ArrowLeft, Download, FileText, Save, Upload } from 'lucide-react';
import Papa from 'papaparse';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import {
  useCommissionSetDetail,
  useSetCommissionItems,
  useSetSelfDevelopedSaAdditionRate,
} from '@/hooks/useCommissions';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useCatalogItemsForCommissionSet } from '@/hooks/useProductCatalogs';
import { paths } from '@/routes/paths';

import { filterItemsForCategory } from './categoryCatalogLink';
import { CommissionTableViewerDialog } from './CommissionTableViewerDialog';

export function CommissionDetailPage() {
  const { setId = '', categoryId = '' } = useParams<{
    setId: string;
    categoryId: string;
  }>();
  const navigate = useNavigate();
  const detail = useCommissionSetDetail(setId);
  const categories = useProductCategories();
  // v3.2.30+: 季節モードではなく、このセットに紐付くカタログの価格を参照する
  const allCatalogItems = useCatalogItemsForCommissionSet(setId);
  const [activeTab, setActiveTab] = useState<CommissionListType>('referrer');

  if (detail.isLoading || categories.isLoading || allCatalogItems.isLoading) {
    return (
      <Screen title="読み込み中…">
        <Spinner />
      </Screen>
    );
  }
  if (!detail.data) {
    return (
      <Screen title="報酬セットが見つかりません">
        <Button onClick={() => navigate(paths.commissions)}>
          <ArrowLeft className="size-3.5" /> 一覧に戻る
        </Button>
      </Screen>
    );
  }

  const category = (categories.data ?? []).find((c) => c.id === categoryId);
  if (!category) {
    return (
      <Screen title="カテゴリーが見つかりません">
        <Button
          onClick={() =>
            navigate(paths.commissionSetCategories.replace(':setId', setId))
          }
        >
          <ArrowLeft className="size-3.5" /> セット詳細に戻る
        </Button>
      </Screen>
    );
  }

  const { set, lists } = detail.data;

  // このカテゴリーに紐づく catalog の items を全件抽出 (v3.2.10: catalogIds 優先、同名一致フォールバック)
  const categoryItems = filterItemsForCategory(allCatalogItems.data ?? [], category);

  return (
    <DetailPageInner
      set={set}
      lists={lists}
      category={category}
      categoryItems={categoryItems}
      setId={setId}
      categoryId={categoryId}
      navigate={navigate}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
}

function DetailPageInner({
  set,
  lists,
  category,
  categoryItems,
  setId,
  categoryId,
  navigate,
  activeTab,
  setActiveTab,
}: {
  set: ReturnType<typeof useCommissionSetDetail>['data'] extends infer T
    ? T extends { set: infer S }
      ? S
      : never
    : never;
  lists: ReturnType<typeof useCommissionSetDetail>['data'] extends infer T
    ? T extends { lists: infer L }
      ? L
      : never
    : never;
  category: NonNullable<ReturnType<typeof useProductCategories>['data']>[number];
  categoryItems: ReturnType<typeof filterItemsForCategory>;
  setId: string;
  categoryId: string;
  navigate: ReturnType<typeof useNavigate>;
  activeTab: CommissionListType;
  setActiveTab: (t: CommissionListType) => void;
}) {
  // v3.2.29+: 報酬表ビューア モーダル
  const [viewerOpen, setViewerOpen] = useState(false);

  return (
    <Screen
      title={`${set.name} / ${category.name}`}
      description={`商品マスタ ${categoryItems.length} 件すべてに報酬を設定してください。タブで対象 (紹介者・SA・社員) を切替えできます。`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setViewerOpen(true)}
            disabled={categoryItems.length === 0}
            title="ロール別の報酬表をモーダルで表示 / PDF 出力"
          >
            <FileText className="size-3.5" />
            報酬表を表示
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              navigate(paths.commissionSetCategories.replace(':setId', setId))
            }
          >
            <ArrowLeft className="size-3.5" /> セット詳細に戻る
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* タブ切替 */}
        <div className="flex items-center gap-1 border-b border-[var(--border)]">
          {COMMISSION_LIST_TYPES.map((lt) => (
            <button
              key={lt}
              type="button"
              onClick={() => setActiveTab(lt)}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                activeTab === lt
                  ? 'border-[var(--accent)] text-[var(--accent)] font-medium'
                  : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
              }`}
            >
              {COMMISSION_LIST_LABELS[lt]}
            </button>
          ))}
        </div>

        {categoryItems.length === 0 ? (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-6 text-center text-xs text-[var(--text-muted)] italic">
            このカテゴリ「{category.name}」と同名の商品カタログ (product_catalogs) がありません。
            <br />
            商品マスタ画面でカテゴリ名と同じ category 名のカタログを作成してください。
          </div>
        ) : (
          <>
            {/* v3.3.0+: SA タブのみ、カテゴリー単位の「開拓顧客加算率」を編集 */}
            {activeTab === 'sa' && (
              <SelfDevelopedAdditionEditor
                setId={setId}
                categoryId={categoryId}
                categoryName={category.name}
                initialRate={resolveSelfDevelopedAdditionRate(set, categoryId)}
              />
            )}
            <ListEditor
              key={`${activeTab}-${categoryId}`}
              setId={setId}
              setName={set.name}
              categoryName={category.name}
              listType={activeTab}
              categoryProductIds={new Set(categoryItems.map((it) => it.id))}
              categoryItems={categoryItems.map((it) => ({
                productId: it.id,
                catalogId: it.catalogId,
                productName: it.name,
                modelName: it.modelName,
                priceExcludingTax: it.priceExcludingTax,
                sku: it.sku,
              }))}
              initialDefaultType={lists[activeTab]?.defaultType ?? 'rate'}
              initialFullItems={lists[activeTab]?.items ?? []}
            />
          </>
        )}
      </div>

      {/* v3.2.29+: 報酬表ビューア モーダル */}
      <CommissionTableViewerDialog
        open={viewerOpen}
        onOpenChange={setViewerOpen}
        setId={setId}
        category={category}
      />
    </Screen>
  );
}

// ─────────────────────────────────────────────
// リストエディタ (タブ単位、カテゴリ内全商品を自動表示)
// ─────────────────────────────────────────────

type CategoryProduct = {
  productId: string;
  catalogId: string;
  productName: string;
  modelName: string;
  priceExcludingTax: number;
  sku: string;
};

function ListEditor({
  setId,
  setName,
  categoryName,
  listType,
  categoryProductIds,
  categoryItems,
  initialDefaultType,
  initialFullItems,
}: {
  setId: string;
  setName: string;
  categoryName: string;
  listType: CommissionListType;
  categoryProductIds: Set<string>;
  categoryItems: CategoryProduct[];
  initialDefaultType: CommissionType;
  initialFullItems: CommissionItem[];
}) {
  const save = useSetCommissionItems();

  // セット全体の items[] のうち、本カテゴリ外を保持
  const otherCategoryItems = useMemo(
    () => initialFullItems.filter((it) => !categoryProductIds.has(it.productId)),
    [initialFullItems, categoryProductIds],
  );

  // 本カテゴリ用の items を初期化 (既存があれば値を採用、なければデフォルト 0)
  const [defaultType, setDefaultType] = useState<CommissionType>(initialDefaultType);
  const [items, setItems] = useState<CommissionItem[]>(() =>
    buildCategoryItems(categoryItems, initialFullItems),
  );

  useEffect(() => {
    setDefaultType(initialDefaultType);
    setItems(buildCategoryItems(categoryItems, initialFullItems));
  }, [initialDefaultType, initialFullItems, categoryItems]);

  const isDirty =
    JSON.stringify({ defaultType, items }) !==
    JSON.stringify({
      defaultType: initialDefaultType,
      items: buildCategoryItems(categoryItems, initialFullItems),
    });

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        setId,
        listType,
        defaultType,
        items: [...otherCategoryItems, ...items],
      });
      toast.success(`${COMMISSION_LIST_LABELS[listType]} のリストを保存しました`);
    } catch (err) {
      toast.error(`失敗: ${(err as Error).message}`);
    }
  };

  const updateItem = (productId: string, patch: Partial<CommissionItem>) => {
    setItems(items.map((i) => (i.productId === productId ? { ...i, ...patch } : i)));
  };

  // ─────────────────────────────────────────────
  // CSV エクスポート / インポート
  // ─────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleExportCsv = () => {
    const headers = [
      'productId',
      'sku',
      'productName',
      'priceExcludingTax',
      'type',
      'value',
      'additionalAmount',
      'discountAmount',
    ];
    const rows = items.map((it) => {
      const meta = categoryItems.find((m) => m.productId === it.productId);
      return [
        it.productId,
        meta?.sku ?? '',
        it.productName,
        meta?.priceExcludingTax ?? '',
        it.type === 'rate' ? '%' : it.type === 'fixed' ? '円' : '',
        it.value,
        it.additionalAmount,
        it.discountAmount,
      ];
    });
    const csv = Papa.unparse({ fields: headers, data: rows });
    // Excel での文字化け防止のため UTF-8 BOM を付与
    const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date()
      .toISOString()
      .replace(/[:T]/g, '-')
      .slice(0, 16);
    const safe = (s: string) => s.replace(/[\s/\\?%*:|"<>]/g, '_').slice(0, 40);
    a.href = url;
    a.download = `commission_${safe(setName)}_${safe(categoryName)}_${listType}_${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${items.length} 件をテンプレート出力しました`);
  };

  const handleImportCsv = (file: File) => {
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data ?? [];
        const byId = new Map(items.map((it) => [it.productId, it]));
        // 別セット(別カタログ)出力の CSV や、カタログ差替え後の古いテンプレートは
        // productId が一致しないため、不変キーである SKU で照合フォールバックする
        const idBySku = new Map<string, string>();
        for (const meta of categoryItems) {
          const sku = (meta.sku ?? '').trim();
          if (!sku) continue;
          // SKU 重複時は照合が曖昧になるため無効化
          idBySku.set(sku, idBySku.has(sku) ? '' : meta.productId);
        }
        let updated = 0;
        let matchedBySku = 0;
        let skippedUnknown = 0;
        const invalidLines: string[] = [];

        rows.forEach((row, idx) => {
          const productId = (row.productId ?? '').trim();
          const sku = (row.sku ?? '').trim();
          if (!productId && !sku) return;
          let target = productId ? byId.get(productId) : undefined;
          if (!target && sku) {
            const mappedId = idBySku.get(sku);
            target = mappedId ? byId.get(mappedId) : undefined;
            if (target) matchedBySku += 1;
          }
          if (!target) {
            skippedUnknown += 1;
            return;
          }

          const rawType = (row.type ?? '').trim();
          let typeVal: CommissionType | null;
          if (rawType === '' || rawType === '既定') {
            typeVal = null;
          } else if (rawType === 'rate' || rawType === '%') {
            typeVal = 'rate';
          } else if (rawType === 'fixed' || rawType === '円') {
            typeVal = 'fixed';
          } else {
            invalidLines.push(`行${idx + 2}: type "${rawType}" が不正`);
            return;
          }

          const toNum = (v: string | undefined): number => {
            if (v == null || v === '') return 0;
            const n = Number(String(v).replace(/,/g, ''));
            return Number.isFinite(n) ? n : 0;
          };

          // SKU 照合の場合 CSV の productId は別カタログの ID なので、
          // 必ず現在カタログ側の target.productId をキーに更新する
          byId.set(target.productId, {
            ...target,
            type: typeVal,
            value: toNum(row.value),
            additionalAmount: toNum(row.additionalAmount),
            discountAmount: toNum(row.discountAmount),
          });
          updated += 1;
        });

        setItems(items.map((it) => byId.get(it.productId) ?? it));

        const messages: string[] = [`${updated} 件を反映しました`];
        if (matchedBySku > 0) messages.push(`うち ${matchedBySku} 行は SKU で照合`);
        if (skippedUnknown > 0) messages.push(`対象外 ${skippedUnknown} 行をスキップ`);
        if (invalidLines.length > 0) messages.push(`不正 ${invalidLines.length} 行`);
        toast.success(messages.join(' / '));
        if (updated === 0 && skippedUnknown > 0) {
          toast.warning(
            '全行がこのセットのカタログと一致しませんでした。別の報酬セットで出力した CSV か、カタログ差替え前の古いテンプレートの可能性があります。この画面の「CSVテンプレートを出力」から出し直すか、SKU 列が現在のカタログと一致しているか確認してください。',
          );
        }
        if (invalidLines.length > 0) {
          toast.warning(invalidLines.slice(0, 5).join('\n'));
        }
        toast.info('「保存」ボタンで確定してください');
      },
      error: (err) => {
        toast.error(`CSV読込に失敗: ${err.message}`);
      },
    });
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImportCsv(file);
    e.target.value = '';
  };

  return (
    <div className="space-y-3 rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Label className="text-xs">既定の報酬タイプ</Label>
          <div className="flex gap-1">
            {(['rate', 'fixed'] as const).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setDefaultType(t)}
                className={`px-3 py-1 text-xs rounded-md border transition-colors ${
                  defaultType === t
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)] text-[var(--accent)]'
                    : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                {t === 'rate' ? 'レート (%)' : '固定額 (円)'}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[var(--text-muted)]">
            各行で個別指定がない場合に採用
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleExportCsv}
            title="現在の値を CSV テンプレートとして出力"
          >
            <Download className="size-3.5" /> CSVテンプレートを出力
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            title="CSV を一括取込（保存ボタンで確定）"
          >
            <Upload className="size-3.5" /> CSV一括入力
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={onFileChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || save.isPending}
          >
            <Save className="size-3.5" />
            {save.isPending ? '保存中…' : isDirty ? '保存' : '保存済'}
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[var(--border)] overflow-hidden">
        <div className="grid grid-cols-[1.6fr_0.6fr_0.7fr_0.7fr_0.7fr_1.4fr] gap-2 px-3 py-1.5 bg-[var(--bg-elevated)] text-[10px] font-medium uppercase tracking-wider text-[var(--text-muted)]">
          <div>商品 (商品マスタ)</div>
          <div>タイプ</div>
          <div>値</div>
          <div>追加</div>
          <div>割引</div>
          <div>計算プレビュー (商品の税抜価格)</div>
        </div>
        {items.map((item) => {
          const meta = categoryItems.find((m) => m.productId === item.productId);
          const basePrice = meta?.priceExcludingTax ?? 1_000_000;
          const calc = calculateCommission({
            basePriceExcludingTax: basePrice,
            item,
            defaultType,
          });
          return (
            <div
              key={item.productId}
              className="grid grid-cols-[1.6fr_0.6fr_0.7fr_0.7fr_0.7fr_1.4fr] gap-2 px-3 py-2 items-center text-xs border-t border-[var(--border)]"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">{item.productName}</div>
                <div className="text-[10px] text-[var(--text-muted)] truncate">
                  {meta?.sku} · 税抜 ¥{basePrice.toLocaleString()}
                </div>
              </div>
              <select
                value={item.type ?? ''}
                onChange={(e) =>
                  updateItem(item.productId, {
                    type: (e.target.value as CommissionType) || null,
                  })
                }
                className="px-1 py-1 text-xs rounded border border-[var(--border)] bg-[var(--bg-input)]"
              >
                <option value="">既定</option>
                <option value="rate">%</option>
                <option value="fixed">円</option>
              </select>
              <Input
                type="number"
                value={item.value}
                onChange={(e) =>
                  updateItem(item.productId, { value: Number(e.target.value) || 0 })
                }
                className="text-xs h-7"
              />
              <Input
                type="number"
                value={item.additionalAmount}
                onChange={(e) =>
                  updateItem(item.productId, {
                    additionalAmount: Number(e.target.value) || 0,
                  })
                }
                className="text-xs h-7"
              />
              <Input
                type="number"
                value={item.discountAmount}
                onChange={(e) =>
                  updateItem(item.productId, {
                    discountAmount: Number(e.target.value) || 0,
                  })
                }
                className="text-xs h-7"
              />
              <div
                className="text-[11px] truncate"
                title={calc.formula}
              >
                <span
                  className={
                    calc.finalAmount > 0
                      ? 'text-[var(--accent-green,_#3aaa55)] font-medium'
                      : 'text-[var(--text-muted)]'
                  }
                >
                  ¥{calc.finalAmount.toLocaleString()}
                </span>
                <span className="text-[10px] text-[var(--text-muted)] ml-1">
                  ({calc.type === 'rate' ? `${item.value}%` : '固定'})
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[10px] text-[var(--text-muted)]">
        商品の追加・削除は商品マスタ画面で行います。マスタの変更はここに自動反映されます。
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────
// v3.3.0+: 開拓顧客加算率エディタ (SA タブのみ表示)
// 旧「弊社紹介減額率」の反転仕様: 弊社紹介=ベースのまま / 開拓顧客=ベース+加算率
// ─────────────────────────────────────────────

function SelfDevelopedAdditionEditor({
  setId,
  categoryId,
  categoryName,
  initialRate,
}: {
  setId: string;
  categoryId: string;
  categoryName: string;
  initialRate: number;
}) {
  const save = useSetSelfDevelopedSaAdditionRate();
  const [rate, setRate] = useState<string>(String(initialRate ?? 0));

  useEffect(() => {
    setRate(String(initialRate ?? 0));
  }, [initialRate, categoryId, setId]);

  const parsed = Number(rate);
  const valid = Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
  const isDirty = valid && parsed !== (initialRate ?? 0);

  const handleSave = async () => {
    if (!valid) {
      toast.error('加算率は 0〜100 の範囲で入力してください');
      return;
    }
    try {
      await save.mutateAsync({
        setId,
        categoryId,
        rate: parsed > 0 ? parsed : null,
      });
      toast.success(
        parsed > 0
          ? `「${categoryName}」の開拓顧客加算率を +${parsed}% に設定しました`
          : `「${categoryName}」の開拓顧客加算率を解除しました`,
      );
    } catch (err) {
      toast.error(`保存失敗: ${(err as Error).message}`);
    }
  };

  return (
    <div className="rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Label className="text-xs font-semibold !mb-0">
          開拓顧客の SA 報酬加算率
        </Label>
        <span className="text-[10px] text-[var(--text-muted)]">
          (カテゴリー「{categoryName}」専用 / SA のみ適用、紹介者・社員は影響なし)
        </span>
      </div>
      <p className="text-[10px] text-[var(--text-muted)]">
        SA 自身が開拓したお客様のケース (弊社紹介フラグの**ない**商談) では、各商品の SA
        報酬率 (%) にここで指定した％を**直接プラス**します（乗算ではなく加算）。
        弊社紹介フラグのある商談は、下の表のベース報酬率のまま計算されます。
        <br />
        例: ベース報酬率 5.5% / 加算率 2% → 開拓顧客は 7.5% で計算。固定額タイプは本加算の対象外。0% で加算なし。
      </p>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          min={0}
          max={100}
          step="0.1"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          className="w-28 text-xs h-7"
        />
        <span className="text-xs">%</span>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || save.isPending}
        >
          <Save className="size-3.5" />
          {save.isPending ? '保存中…' : isDirty ? '加算率を保存' : '保存済'}
        </Button>
        {parsed > 0 && valid && (
          <span className="text-[11px] text-[var(--accent-green,_#3aaa55)] ml-2">
            適用例: ベース報酬率 5.5% の商品 → 開拓顧客は {Math.min(100, Math.round((5.5 + parsed) * 100) / 100)}% で計算
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * カテゴリの全商品マスタを基にした「編集対象 items[]」を構築。
 * 既存セットに同 productId があれば値を採用、なければデフォルト 0 で初期化。
 */
function buildCategoryItems(
  categoryItems: CategoryProduct[],
  existingItems: CommissionItem[],
): CommissionItem[] {
  const existingMap = new Map(existingItems.map((it) => [it.productId, it]));
  return categoryItems.map((m): CommissionItem => {
    const existing = existingMap.get(m.productId);
    if (existing) {
      // productName を最新マスタで上書き (商品名変更を反映)
      return { ...existing, productName: m.productName, catalogId: m.catalogId };
    }
    return {
      productId: m.productId,
      catalogId: m.catalogId,
      productName: m.productName,
      type: null,
      value: 0,
      additionalAmount: 0,
      discountAmount: 0,
    };
  });
}
